import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle
} from '@/shared/components/ui/Card';

const games = [
  {
    title: 'Incrementalist',
    description:
      'An incremental roguelike where you must keep your fire alive to hold back the consuming darkness.',
    status: 'Coming Soon',
    image: null
  },
  {
    title: 'Knight Survivor',
    description:
      'Defend your base from relentless nightly attacks in this intense online multiplayer survival game.',
    status: 'Live Now',
    link: 'https://knightsurvivor.io',
    image: '/ks-ss.png'
  },
  {
    title: 'Deeper Darker Danger',
    description:
      'Dive into a deep-sea adventure. Team up with friends to pilot a submarine and survive the ocean depths.',
    status: 'Coming Later',
    image: null
  }
];

const structuredData = {
  '@context': 'https://schema.org',
  '@graph': [
    {
      '@type': 'Organization',
      name: 'Just One Week Games',
      url: 'https://jowgames.org',
      logo: 'https://jowgames.org/JOW%20Games%20Pixelated.png',
      email: 'info@jowgames.org',
      description:
        'We are 3 Australian game developers who love making games. Creators of Knight Survivor, Incrementalist, and Subcraft 3D.'
    },
    {
      '@type': 'VideoGame',
      name: 'Knight Survivor',
      description:
        'Defend your base from relentless nightly attacks in this intense online multiplayer survival game.',
      applicationCategory: 'Game',
      operatingSystem: 'Web Browser',
      author: {
        '@type': 'Organization',
        name: 'Just One Week Games'
      },
      url: 'https://knightsurvivor.io'
    }
  ]
};

const HomePage = () => {
  return (
    <div className="relative min-h-screen bg-background-primary text-text-primary antialiased flex flex-col font-sans overflow-hidden">
      <Helmet>
        <title>Just One Week Games | Indie Game Developers</title>
        <meta
          name="description"
          content="Just One Week (JOW) Games - Australian indie game developers creating unique experiences like Knight Survivor, Incrementalist, and Deeper Darker Danger."
        />
        <meta
          name="keywords"
          content="JOW Games, Indie Games, Game Development, Knight Survivor, Incrementalist, Subcraft 3D, Browser Games, Multiplayer Games"
        />
        <link rel="canonical" href="https://jowgames.org" />

        {/* Open Graph */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://jowgames.org" />
        <meta
          property="og:title"
          content="Just One Week Games | Indie Game Developers"
        />
        <meta
          property="og:description"
          content="Just One Week (JOW) Games - Australian indie game developers creating unique experiences like Knight Survivor, Incrementalist, and Deeper Darker Danger."
        />
        <meta
          property="og:image"
          content="https://jowgames.org/JOW%20Games%20Pixelated.png"
        />

        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta
          name="twitter:title"
          content="Just One Week Games | Indie Game Developers"
        />
        <meta
          name="twitter:description"
          content="Just One Week (JOW) Games - Australian indie game developers creating unique experiences like Knight Survivor, Incrementalist, and Deeper Darker Danger."
        />
        <meta
          name="twitter:image"
          content="https://jowgames.org/JOW%20Games%20Pixelated.png"
        />

        {/* Structured Data */}
        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
      </Helmet>

      {/* Background Video */}
      <div className="fixed inset-0 z-0 pointer-events-none">
        <div className="absolute inset-0 bg-background-primary/80 z-10" />
        <video
          autoPlay
          loop
          muted
          playsInline
          preload="metadata"
          poster="/video-poster.png"
          className="w-full h-full object-cover opacity-50"
        >
          <source src="/bg-optimized.mp4" type="video/mp4" />
        </video>
      </div>

      <div className="relative z-10 flex flex-col min-h-screen">
        <main className="container mx-auto px-4 py-12 flex-grow flex flex-col items-center">
          {/* Logo Section */}
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="mb-16 text-center"
          >
            <h1 className="sr-only">Just One Week Games</h1>
            <img
              src="/JOW Games Pixelated.png"
              alt="Just One Week Games"
              className="h-32 md:h-48 mx-auto object-contain"
            />
          </motion.div>

          {/* Games Section */}
          <section className="w-full max-w-5xl mb-24">
            <motion.h2
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.3 }}
              className="text-3xl font-bold text-center mb-10 text-accent-primary drop-shadow-md"
            >
              Our Games
            </motion.h2>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {games.map((game, index) => (
                <motion.div
                  key={game.title}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.4 + index * 0.2 }}
                >
                  {game.link ? (
                    <a
                      href={game.link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="block h-full group"
                    >
                      <Card className="h-full hover:shadow-[0_0_20px_rgba(45,226,230,0.2)] transition-all duration-300 border-accent-info/30 hover:border-accent-info bg-background-card/80 backdrop-blur-sm cursor-pointer group-hover:-translate-y-1 overflow-hidden">
                        {game.image ? (
                          <div className="w-full h-48 overflow-hidden">
                            <img
                              src={game.image}
                              alt={game.title}
                              className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                            />
                          </div>
                        ) : (
                          <div className="w-full h-48 bg-background-secondary/50 flex items-center justify-center group-hover:bg-background-secondary/70 transition-colors duration-300">
                            <span className="text-4xl opacity-20">🎮</span>
                          </div>
                        )}
                        <CardHeader>
                          <CardTitle className="text-xl text-center text-accent-secondary group-hover:text-accent-primary transition-colors">
                            {game.title}
                          </CardTitle>
                        </CardHeader>
                        <CardContent>
                          <p className="text-text-secondary text-center">
                            {game.description}
                          </p>
                          <div className="mt-4 text-center">
                            <span
                              className={`inline-block px-3 py-1 text-xs font-semibold rounded-full ${
                                game.status === 'Live Now'
                                  ? 'bg-accent-success/20 text-accent-success'
                                  : 'bg-accent-primary text-text-inverse'
                              }`}
                            >
                              {game.status}
                            </span>
                          </div>
                        </CardContent>
                      </Card>
                    </a>
                  ) : (
                    <Card className="h-full hover:shadow-[0_0_20px_rgba(45,226,230,0.2)] transition-all duration-300 border-accent-info/30 hover:border-accent-info bg-background-card/80 backdrop-blur-sm overflow-hidden">
                      {game.image ? (
                        <div className="w-full h-48 overflow-hidden">
                          <img
                            src={game.image}
                            alt={game.title}
                            className="w-full h-full object-cover"
                          />
                        </div>
                      ) : (
                        <div className="w-full h-48 bg-background-secondary/50 flex items-center justify-center">
                          <span className="text-4xl opacity-20">🎮</span>
                        </div>
                      )}
                      <CardHeader>
                        <CardTitle className="text-xl text-center text-accent-secondary">
                          {game.title}
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        <p className="text-text-secondary text-center">
                          {game.description}
                        </p>
                        <div className="mt-4 text-center">
                          <span className="inline-block px-3 py-1 text-xs font-semibold rounded-full bg-accent-primary text-text-inverse">
                            {game.status}
                          </span>
                        </div>
                      </CardContent>
                    </Card>
                  )}
                </motion.div>
              ))}
            </div>
          </section>

          {/* About Us Section */}
          <section className="w-full max-w-3xl text-center mb-12">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="bg-background-card/60 backdrop-blur-sm p-8 rounded-2xl border border-accent-secondary/20"
            >
              <h2 className="text-2xl font-bold mb-4 text-accent-primary">
                About Us
              </h2>
              <p className="text-lg text-text-secondary leading-relaxed">
                We are Just One Week (JOW) Games, a team of 3 Australian
                developers who love making games.
              </p>
            </motion.div>
          </section>
        </main>

        <footer className="container mx-auto px-4 py-6 flex flex-col md:flex-row items-center justify-between text-text-secondary text-sm">
          <div className="order-2 md:order-1 mt-4 md:mt-0 text-center md:text-left">
            <p>
              &copy; {new Date().getFullYear()} Just One Week Games. All rights
              reserved.
            </p>
            <div className="mt-1 flex gap-2 justify-center md:justify-start flex-wrap">
              <p>
                Contact us at:{' '}
                <a
                  href="mailto:info@jowgames.org"
                  className="text-accent-info hover:text-accent-primary transition-colors"
                >
                  info@jowgames.org
                </a>
              </p>
              <span className="hidden md:inline">|</span>
              <Link
                to="/terms"
                className="hover:text-accent-primary transition-colors"
              >
                Terms of Service
              </Link>
              <span className="hidden md:inline">|</span>
              <Link
                to="/privacy"
                className="hover:text-accent-primary transition-colors"
              >
                Privacy Policy
              </Link>
            </div>
          </div>
          <div className="flex items-center gap-4 order-1 md:order-2">
            <span className="text-xs opacity-70">Powered by</span>
            <a
              href="https://bevyengine.org"
              target="_blank"
              rel="noopener noreferrer"
              className="hover:opacity-80 transition-opacity"
              title="Bevy Engine"
            >
              <img src="/bevy-logo.svg" alt="Bevy" className="h-8 w-auto" />
            </a>
            <a
              href="https://www.rust-lang.org"
              target="_blank"
              rel="noopener noreferrer"
              className="hover:opacity-80 transition-opacity"
              title="Rust"
            >
              <img
                src="/rust-logo.svg"
                alt="Rust"
                className="h-8 w-auto invert"
              />
            </a>
          </div>
        </footer>
      </div>
    </div>
  );
};

export default HomePage;
